/**
 * B6 · The levels path — "the feeling that it's a level-based game".
 *
 * A single-column **vertical** snake. On mobile horizontal scroll is
 * forbidden (repo rule), so the snake's amplitude is applied with
 * `transform: translateX(%)` over rows that already fit: the offset gives a
 * sense of a path without pushing anything outside the viewport, and the container clips
 * just in case.
 *
 * The connector tells the story of the graph: solid when the prerequisite is hard,
 * **dashed** when there's an alternative path. That's what draws the level
 * jump without this component knowing what a jump is — it only paints the
 * edges the server derived from the configurations.
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Map as MapIcon } from "@fleet/icons";
import type { StageLabel } from "@space/shared";
import { cn } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@fleet/ui";
import { type PathRow, toPathRows, useTrackState } from "./use-progression";
import { StageNode } from "./stage-node";

/**
 * What THIS academy calls a level. The enum comes translated out of the box; the
 * free text (`custom`) belongs to the academy and is deliberately not translated: opening
 * the 11 locales to user text would be an uncontrollable i18n hole.
 */
function stageLabelFor(
  t: ReturnType<typeof useI18n>["t"],
  label: StageLabel,
  custom: string | null | undefined,
  position: number,
): string {
  const n = position + 1;
  switch (label) {
    case "module": {
      return t(msg`Module ${n}`);
    }
    case "cycle": {
      return t(msg`Cycle ${n}`);
    }
    case "term": {
      return t(msg`Term ${n}`);
    }
    case "stage": {
      return t(msg`Stage ${n}`);
    }
    case "custom": {
      return custom ? `${custom} ${n}` : t(msg`Level ${n}`);
    }
    default: {
      return t(msg`Level ${n}`);
    }
  }
}

const Connector: Component<{ link: PathRow["link"] }> = (props) => (
  <Show when={props.link !== "none"}>
    <div
      aria-hidden="true"
      class={cn(
        props.link === "soft" ? "path-link--soft" : "path-link",
        props.link === "done" && "path-link--done",
      )}
    />
  </Show>
);

export const PathMap: Component<{
  trackId: string;
  onOpenStage?: (stageId: string, status: string) => void;
}> = (props) => {
  const { t } = useI18n();
  const state = useTrackState(() => props.trackId);
  const rows = (): PathRow[] => toPathRows(state.data);

  return (
    <div class="w-full overflow-x-hidden" data-testid="progression-path">
      <Show
        when={!state.isLoading}
        fallback={
          <div class="path-track" aria-busy="true">
            <For each={[0, 1, 2]}>{() => <Skeleton class="mb-4 h-24 w-full max-w-xs" />}</For>
          </div>
        }
      >
        <Show
          when={rows().length > 0}
          fallback={
            <EmptyState
              icon={MapIcon}
              title={t(msg`No path yet`)}
              description={t(
                msg`Your academy hasn't published a learning path for this programme yet.`,
              )}
            />
          }
        >
          <Show when={state.data?.prerequisitesEnforced === false}>
            <p class="mb-4 text-center text-sm text-muted-foreground">
              {t(msg`Every level is open right now — your academy turned prerequisites off.`)}
            </p>
          </Show>
          <div class="path-track">
            <For each={rows()}>
              {(row, index) => (
                <>
                  <Connector link={row.link} />
                  <div
                    class={cn(
                      "path-row",
                      row.side === "left" && "path-row--left",
                      row.side === "right" && "path-row--right",
                    )}
                  >
                    <StageNode
                      title={row.title}
                      status={row.status}
                      progressPct={row.progressPct}
                      blockedBy={row.blockedBy}
                      testid={`stage-node-${index()}`}
                      stageLabel={stageLabelFor(
                        t,
                        state.data?.stageLabel ?? "level",
                        state.data?.stageLabelCustom,
                        index(),
                      )}
                      onActivate={() => props.onOpenStage?.(row.stageId, row.status)}
                    />
                  </div>
                </>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
};
